import { Suspense, lazy, useEffect } from 'react';
import 'react-photo-view/dist/react-photo-view.css';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
    APP_GIT_BRANCH,
    APP_GIT_COMMIT,
    APP_URL_ACTIVITIES,
    APP_URL_ACTIVITY,
    APP_URL_CATALOG,
    APP_URL_CHAT,
    APP_URL_CHAT_HISTORY,
    APP_URL_CHAT_SHARE,
    APP_URL_HOME,
    APP_URL_NOT_FOUND,
    APP_URL_PAYMENT,
    APP_URL_PAYMENT_HISTORY,
    APP_URL_PAYMENT_RESULT,
    APP_URL_PROFILE,
    APP_URL_REDIRECT_CHAT,
    APP_URL_SUBSCRIPTION,
    APP_URL_USER_DELETION_STATUS,
    APP_URL_WIDGET_CHAT
} from '../const';
import useABBannerTesting from '../hooks/useABBannerTesting';
import useAuth from '../hooks/useAuth';
import useCatchRedirect from '../hooks/useCatchRedirect';
import usePayments from '../hooks/usePayments';
import { useWindowSize } from '../hooks/useWindowSize';
import CheckoutRisk from '../service/CheckoutRisk';
import ResponseService from '../service/ResponseService';
import { getUser } from '../store/slices/authSlice';
import GoogleAnalytics from '../utils/GoogleAnalytics';
import { appHeight, setMetaViewportContent, setRetinaClass } from '../utils/helper';
import ProtectedRoute from './ProtectedRoute';
import Spinner from './common/Spinner';
import UpdateRemoveChatModal from './common/UpdateRemoveChatModal';
import ActivityLanding from './pages/ActivitiesLandings';
import ActivitiesPage from './pages/ActivityPage';
import AdvisorLanding from './pages/AdvisorLandings';
import AuthModal from './pages/AuthProvider/AuthModal';
import PaymentModal from './pages/PaymentProvider/PaymentModal';

const ChatPage = lazy(() => import('./pages/ChatPage'));
const ChatHistoryPage = lazy(() => import('./pages/ChatHistoryPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const Profile = lazy(() => import('./pages/Profile'));
const Page404 = lazy(() => import('./pages/Page404'));
const PageUserDeletionStatus = lazy(() => import('./pages/PageUserDeletionStatus'));
const WidgetChatRedirectPage = lazy(() => import('./pages/WidgetChatRedirectPage'));
const ChatRedirectPage = lazy(() => import('./pages/ChatPage/ChatRedirectPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage'));
const PaymentResultPage = lazy(() => import('./pages/PaymentResultPage'));

const protectedRoutsConfig = [
    {
        path: APP_URL_CHAT_HISTORY,
        Element: ChatHistoryPage
    },
    {
        path: APP_URL_PROFILE,
        Element: Profile
    },
    {
        path: APP_URL_SUBSCRIPTION,
        Element: SubscriptionPage
    },
    {
        path: `${APP_URL_PAYMENT}`,
        Element: PaymentPage
    },
    {
        path: `${APP_URL_PAYMENT_HISTORY}`,
        Element: PaymentHistoryPage
    },
    {
        path: `${APP_URL_PAYMENT_RESULT}/:type`,
        Element: PaymentResultPage
    }
];

const App = () => {
    const [_, height] = useWindowSize();
    const user = useSelector(getUser);
    const isUserAuthorized = user?.id;

    appHeight(height);

    useEffect(() => {
        ResponseService.init();
        GoogleAnalytics.init();
        setRetinaClass();
        setMetaViewportContent();
        CheckoutRisk.init();

        if (APP_GIT_COMMIT && APP_GIT_BRANCH) {
            console.log(`APP Version: ${APP_GIT_BRANCH}-${APP_GIT_COMMIT}`);
        }

        return () => {
            CheckoutRisk.removeListener();
        };
    }, []);

    useAuth();
    useABBannerTesting();
    usePayments();
    useCatchRedirect();

    return (
        <>
            <Suspense
                fallback={
                    <div className="overlay">
                        <Spinner />
                    </div>
                }
            >
                <main className="page">
                    <Routes>
                        <Route index path={APP_URL_HOME} element={<LandingPage />} exact />
                        <Route path={`${APP_URL_REDIRECT_CHAT}/:chatId`} element={<ChatRedirectPage useNewChatUrl={true} />} />
                        <Route path={`${APP_URL_CHAT}/:alias/:chatId`} element={<ChatPage />} />
                        <Route path={APP_URL_CATALOG} element={<CatalogPage />} />
                        <Route path={`${APP_URL_CATALOG}/:category`} element={<CatalogPage />} />
                        <Route path={`${APP_URL_ACTIVITIES}`} element={<ActivitiesPage />} />
                        <Route path={`${APP_URL_ACTIVITIES}/:category`} element={<ActivitiesPage />} />
                        <Route path={`${APP_URL_ACTIVITY}/:activityId`} element={<ActivityLanding />} />
                        <Route path={`${APP_URL_CHAT_SHARE}/:alias/:chatId`} element={<ChatPage />} />
                        <Route
                            path={`${APP_URL_CHAT_SHARE}/:chatId`}
                            element={<ChatRedirectPage wasShared={true} useNewChatUrl={true} />}
                        />
                        <Route path={APP_URL_REDIRECT_CHAT} element={<ChatRedirectPage />} />
                        <Route path={`${APP_URL_WIDGET_CHAT}/:alias`} element={<WidgetChatRedirectPage />} />
                        <Route path={`/${APP_URL_NOT_FOUND}`} element={<Page404 />} />
                        <Route path={`/${APP_URL_USER_DELETION_STATUS}`} element={<PageUserDeletionStatus />} />

                        {protectedRoutsConfig.map((rout) => {
                            const { path, Element } = rout;
                            return (
                                <Route
                                    key={path.toString()}
                                    path={path}
                                    element={
                                        <ProtectedRoute user={!!isUserAuthorized}>
                                            <Element />
                                        </ProtectedRoute>
                                    }
                                    exact
                                />
                            );
                        })}
                        <Route path={`${APP_URL_CHAT}/:advisorAlias`} element={<AdvisorLanding />} />
                        <Route path="*" element={<Page404 />} />
                    </Routes>
                </main>
            </Suspense>
            <AuthModal />
            {isUserAuthorized && <PaymentModal />}
            <UpdateRemoveChatModal />
            <ToastContainer />
        </>
    );
};

export default App;
